// Discovery and the connection state machine (protocol §3, ui-flow §3, §9).
// - HTTP: /api/info, /api/find, /api/party (M3).
// - `Connector`: one connection attempt = find (or party lookup) → open the socket → WELCOME
//   within 5 s. Retries after a failed attempt or an unexpected close wait 0.25, 0.5, 1, 2, 4 s
//   (±20 %); after the 5th failed retry it gives up with 'cantReach'. Close codes map to
//   reactions (ui-flow §9 table): 1000 none; 4001 'outdated'; 4002 one automatic re-find then
//   'roomFull'; 4006 retry with a 2 s first wait; 4007 'partyGone'; 4008 'kicked'; 4009
//   'tooMany'; anything else retry.
// - Money target (`{ kind: 'money', stakeUsd }`, money-mode §1.2): the lookup is
//   /api/money/find?stake= (`full` → see below, `disabled` → 'moneyOff'); closes 4011–4016
//   (the money refusals that follow ERROR 11–16, which the app shows itself) give up with
//   'moneyRefused' instead of retrying; a dropped money socket is retried like any other.
// - Practice target (`{ kind: 'practice' }`, money-mode §1.4): /api/practice/find (`full` →
//   retried here like free play, `disabled` → 'practiceOff'); anonymous (no token), PLAY with
//   no ticket.
// - Sign-in (protocol §3.2): a money target's HELLO carries the access token
//   (`ConnectorOptions.authToken`); free targets never send one. A 4017 (ERROR 17 AUTH_INVALID)
//   asks for a fresh token and reconnects once; a second 4017 in a row gives up with
//   'authInvalid' (the app signs out and says so).
//
// - Servers full (architecture §6.3 capacity guard): `/api/find` or `/api/money/find` answering
//   `full` is not an error. The connector emits `full { retry, delayMs }` and asks again after
//   `fullRetryDelayMs` (at least the server's `retryAfterSec`, growing ×1.5 per answer up to 30 s,
//   +0–20 % jitter), for as long as it takes; the app shows "Servers are full · Retrying in N s".
//   These waits do not count toward the 5 failed retries, and a `full` answer resets that count
//   (the server did answer: a lookup dropped now and then during a long wait is not "can't
//   reach"). A money target answering `full` does not wait here: the connector stops
//   (`full { retrying: false }`) and the app shows the money card's waiting state (with Cancel)
//   and goes back to the free arena while it asks again itself.
//
// Details the tables leave open (decided here):
// - A party lookup answering `full` gives up with 'roomFull' ("Every room is full"); a party
//   lookup answering `notFound` gives up with 'partyGone'.
// - The 5 s attempt timeout covers the whole attempt (lookup, open, WELCOME).
// - After a 4006 every wait is at least 2 s (the first one is exactly the spec's 2 s).
// - "A second 4002 in a row": the streak ends when the replacement connection succeeds — its
//   WELCOME for a 4002 at join, or a SPAWNED for a 4002 that answered a PLAY (the app re-sends
//   PLAY after `connected { afterRoomFull: true }`) — or on any other close code.
// - A 4002 is not reported as `lost` (no retry wait follows; the re-find starts at once).
// - A 1000 close made by this client outside the connector (`socket.close()`) stops it with no
//   reaction; a 1000 from the server is unexpected and retried like any unlisted code.
//
// Implementer: client net/game. Consumers: app.ts (connect / retry, maps `gaveUp` reasons to
// dialogs, re-sends PLAY after a 4002 move), ui (player counts from /api/info).

import { CLOSE, isModeId, type ModeId, NET_TIMING, type WelcomeMessage } from '@ballio/shared';
import { type AuthInfo, parseAuthInfo } from '../auth/auth.js';
import { findMoneyRoom, findPracticeRoom } from './money-api.js';
import { serverUrl } from './server-url.js';
import type { GameSocket, SocketCloseInfo } from './socket.js';
import { Emitter } from './socket.js';

// --- HTTP API response shapes (protocol §3; identical to server/src/http/api.ts) --------------

export interface ApiRegion {
  readonly id: string;
  readonly name: string;
  readonly players: number;
  readonly url: string;
}

export interface ApiModeInfo {
  readonly enabled: boolean;
  readonly players: number;
  readonly humans: number;
  readonly rooms: number;
  /** /api/find would answer ok now (true when an older server leaves it out). */
  readonly available: boolean;
}

/** `/api/info.capacity`: the server's capacity guard (architecture §6.3). */
export interface ApiCapacityInfo {
  readonly level: 'ok' | 'shedding' | 'noNewRooms' | 'freeFull' | 'full';
  /** Free play is admitted. */
  readonly free: boolean;
  /** Money rooms are admitted. */
  readonly money: boolean;
  readonly pressure: number;
  readonly retryAfterSec: number;
}

export interface ApiInfoResponse {
  readonly name: string;
  readonly version: string;
  readonly protocol: number;
  readonly sourceUrl: string;
  readonly regions: readonly ApiRegion[];
  readonly modes: Partial<Record<ModeId, ApiModeInfo>>;
  /**
   * `/api/info.online`: real people connected to the server (playing, spectating or on the menu,
   * every room; bots excluded). Null when the server does not report it.
   */
  readonly online: number | null;
  /** Null when the server does not report it. */
  readonly capacity: ApiCapacityInfo | null;
  /** `/api/info.auth`: the sign-in provider the server verifies; null = no sign-in there. */
  readonly auth: AuthInfo | null;
  /** `/api/info.practice` (money-mode §1.4); null when the server does not report it. */
  readonly practice: ApiPracticeInfo | null;
}

/** `/api/info.practice`: the free play-money arena (money-mode §1.4). */
export interface ApiPracticeInfo {
  readonly enabled: boolean;
  /** /api/practice/find would answer ok now. */
  readonly available: boolean;
  /** The money room it copies, USD (the "$1" on screen). */
  readonly stakeUsd: number;
  readonly humans: number;
  /** Humans playing in the room a find would join now. */
  readonly joinHumans: number;
  readonly maxPlayers: number;
  readonly full: boolean;
}

/**
 * A `full` answer. `retryAfterSec`: the wait the server asks for; `reason: 'capacity'`: the
 * process is at its budget (otherwise every room is at its cap). Both absent on older servers.
 */
export interface ApiFull {
  readonly status: 'full';
  readonly retryAfterSec?: number;
  readonly reason?: 'capacity';
}

export type ApiFindResponse =
  { readonly status: 'ok'; readonly roomId: string; readonly wsUrl: string } | ApiFull;

export type ApiPartyLookupResponse =
  | { readonly status: 'ok'; readonly roomId: string; readonly wsUrl: string }
  | { readonly status: 'notFound' }
  | ApiFull;

export type ApiPartyCreateResponse =
  | {
      readonly status: 'ok';
      readonly code: string;
      readonly roomId: string;
      readonly wsUrl: string;
    }
  | { readonly status: 'full' }
  | { readonly status: 'rateLimited' };

export interface HttpOptions {
  /** Server base (net/server-url.ts); `""` = this page. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** Abort after this many ms. Default 5000. */
  readonly timeoutMs?: number;
}

/** A non-2xx answer the endpoint does not define (or a malformed body). */
export class HttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

/** Default HTTP timeout (protocol §3: an attempt fails after 5 s). */
export const HTTP_TIMEOUT_MS = NET_TIMING.connectAttemptTimeoutSec * 1000;

// --- HTTP plumbing ----------------------------------------------------------------------------

/** HttpOptions plus the timer hooks a Connector passes down (tests inject fake timers). */
interface RequestOptions extends HttpOptions {
  readonly setTimeout?: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout?: (handle: unknown) => void;
}

interface HttpResult {
  readonly status: number;
  readonly ok: boolean;
  /** Parsed JSON body, or undefined when it was missing or not JSON. */
  readonly body: unknown;
}

function apiUrl(path: string, opts?: HttpOptions): string {
  return serverUrl(opts?.baseUrl ?? '', path);
}

async function request(path: string, opts: RequestOptions | undefined, init?: RequestInit) {
  const url = apiUrl(path, opts);
  const doFetch: typeof fetch =
    opts?.fetchImpl ?? ((input, reqInit) => globalThis.fetch(input, reqInit));
  const timeoutMs = opts?.timeoutMs ?? HTTP_TIMEOUT_MS;
  const setT = opts?.setTimeout ?? ((fn: () => void, ms: number) => globalThis.setTimeout(fn, ms));
  const clearT =
    opts?.clearTimeout ??
    ((h: unknown) => globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>));
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  let handle: unknown = null;
  const timeout = new Promise<never>((_, reject) => {
    handle = setT(() => {
      ctrl?.abort();
      reject(new HttpError(`${path}: timed out after ${timeoutMs} ms`, 0));
    }, timeoutMs);
  });
  try {
    // No `cache` option: the API answers with Cache-Control: no-store, and some fetch
    // implementations (Node's) reject cache modes.
    const res = await Promise.race([
      doFetch(url, { ...init, signal: ctrl?.signal ?? null }),
      timeout,
    ]);
    let body: unknown = undefined;
    try {
      body = await Promise.race([res.json() as Promise<unknown>, timeout]);
    } catch {
      body = undefined;
    }
    return { status: res.status, ok: res.ok, body } satisfies HttpResult;
  } finally {
    clearT(handle);
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** A `{ status: "full", retryAfterSec?, reason? }` body as ApiFull (unknown fields dropped). */
export function parseFull(b: Record<string, unknown> | null): ApiFull {
  const out: { status: 'full'; retryAfterSec?: number; reason?: 'capacity' } = { status: 'full' };
  const wait = b?.['retryAfterSec'];
  if (typeof wait === 'number' && Number.isFinite(wait) && wait > 0) out.retryAfterSec = wait;
  if (b?.['reason'] === 'capacity') out.reason = 'capacity';
  return out;
}

const CAPACITY_LEVELS: readonly ApiCapacityInfo['level'][] = [
  'ok',
  'shedding',
  'noNewRooms',
  'freeFull',
  'full',
];

function parseCapacity(raw: unknown): ApiCapacityInfo | null {
  const o = asRecord(raw);
  if (!o) return null;
  const level = CAPACITY_LEVELS.find((l) => l === o['level']) ?? 'ok';
  return {
    level,
    free: o['free'] !== false,
    money: o['money'] === true,
    pressure: num(o['pressure']),
    retryAfterSec: num(o['retryAfterSec'], 5),
  };
}

/** `{ status: "ok", roomId, wsUrl }` → the typed value, or null. */
function okRoom(b: Record<string, unknown> | null): { roomId: string; wsUrl: string } | null {
  if (!b || b['status'] !== 'ok') return null;
  const roomId = b['roomId'];
  const wsUrl = b['wsUrl'];
  return typeof roomId === 'string' && typeof wsUrl === 'string' && wsUrl !== ''
    ? { roomId, wsUrl }
    : null;
}

/** `/api/info.practice` → the typed value, or null (absent on an older server, or malformed). */
export function parsePractice(raw: unknown): ApiPracticeInfo | null {
  const o = asRecord(raw);
  if (!o || typeof o['enabled'] !== 'boolean') return null;
  return {
    enabled: o['enabled'],
    available: o['enabled'] && o['available'] !== false,
    stakeUsd: num(o['stakeUsd'], 1) > 0 ? num(o['stakeUsd'], 1) : 1,
    humans: Math.max(0, Math.floor(num(o['humans']))),
    joinHumans: Math.max(0, Math.floor(num(o['joinHumans']))),
    maxPlayers: Math.max(0, Math.floor(num(o['maxPlayers']))),
    full: o['full'] === true,
  };
}

/** `/api/info.online`: a whole count ≥ 0, else null (absent on an older server, or malformed). */
function parseOnline(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
}

/** Validates and normalises an /api/info body (unknown modes and malformed entries dropped). */
export function parseInfo(raw: unknown): ApiInfoResponse {
  const b = asRecord(raw);
  if (!b) throw new HttpError('/api/info: not a JSON object', 200);
  const regions: ApiRegion[] = [];
  if (Array.isArray(b['regions'])) {
    for (const r of b['regions'] as unknown[]) {
      const o = asRecord(r);
      if (!o || typeof o['id'] !== 'string') continue;
      regions.push({
        id: o['id'],
        name: str(o['name'], o['id']),
        players: num(o['players']),
        url: str(o['url']),
      });
    }
  }
  const modes: Partial<Record<ModeId, ApiModeInfo>> = {};
  const m = asRecord(b['modes']);
  if (m) {
    for (const [id, v] of Object.entries(m)) {
      const o = asRecord(v);
      if (!o || !isModeId(id)) continue;
      modes[id] = {
        enabled: o['enabled'] === true,
        players: num(o['players']),
        humans: num(o['humans']),
        rooms: num(o['rooms']),
        available: o['available'] !== false,
      };
    }
  }
  return {
    name: str(b['name']),
    version: str(b['version']),
    protocol: num(b['protocol']),
    sourceUrl: str(b['sourceUrl']),
    regions,
    modes,
    online: parseOnline(b['online']),
    capacity: parseCapacity(b['capacity']),
    auth: parseAuthInfo(b['auth']),
    practice: parsePractice(b['practice']),
  };
}

/** GET /api/info. Rejects on network errors, non-2xx or bad JSON. */
export async function fetchInfo(opts?: HttpOptions): Promise<ApiInfoResponse> {
  const res = await request('/api/info', opts);
  if (!res.ok) throw new HttpError(`/api/info: HTTP ${res.status}`, res.status);
  return parseInfo(res.body);
}

/** GET /api/find?mode=… (200 with `ok` or `full`). Rejects on network errors / non-2xx. */
export async function findRoom(mode: ModeId, opts?: HttpOptions): Promise<ApiFindResponse> {
  const path = `/api/find?mode=${encodeURIComponent(mode)}`;
  const res = await request(path, opts);
  const b = asRecord(res.body);
  if (b?.['status'] === 'full') return parseFull(b);
  if (!res.ok) throw new HttpError(`/api/find: HTTP ${res.status}`, res.status);
  const room = okRoom(b);
  if (!room) throw new HttpError('/api/find: malformed response', res.status);
  return { status: 'ok', ...room };
}

/** M3: GET /api/party/<code> (200 ok, 404 notFound, 409 full). */
export async function lookupParty(
  code: string,
  opts?: HttpOptions,
): Promise<ApiPartyLookupResponse> {
  const res = await request(`/api/party/${encodeURIComponent(code)}`, opts);
  const b = asRecord(res.body);
  if (res.status === 404 || b?.['status'] === 'notFound') return { status: 'notFound' };
  if (res.status === 409 || b?.['status'] === 'full') return parseFull(b);
  if (!res.ok) throw new HttpError(`/api/party: HTTP ${res.status}`, res.status);
  const room = okRoom(b);
  if (!room) throw new HttpError('/api/party: malformed response', res.status);
  return { status: 'ok', ...room };
}

/** M3: POST /api/party (201 ok, 503 full, 429 rateLimited). */
export async function createParty(opts?: HttpOptions): Promise<ApiPartyCreateResponse> {
  const res = await request('/api/party', opts, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const b = asRecord(res.body);
  if (res.status === 503 || b?.['status'] === 'full') return parseFull(b);
  if (res.status === 429 || b?.['status'] === 'rateLimited') return { status: 'rateLimited' };
  if (!res.ok) throw new HttpError(`/api/party: HTTP ${res.status}`, res.status);
  const room = okRoom(b);
  const code = b?.['code'];
  if (!room || typeof code !== 'string') {
    throw new HttpError('/api/party: malformed response', res.status);
  }
  return { status: 'ok', code, ...room };
}

/**
 * The wait before retry number `retry` (1-based): 0.25·2^(retry−1) s ±20 % (`random` in [0, 1)),
 * or 2 s for the first retry after a 4006 close (later ones after a 4006 wait at least 2 s too).
 */
export function retryDelayMs(retry: number, random: () => number, afterShutdown = false): number {
  const n = Math.max(1, Math.floor(retry) || 1);
  const first = NET_TIMING.retryWaitsSec[0] ?? 0.25;
  let baseMs = first * 1000 * 2 ** (n - 1);
  if (afterShutdown) baseMs = Math.max(baseMs, NET_TIMING.shutdownFirstRetrySec * 1000);
  const j = NET_TIMING.retryJitter;
  const u = random();
  const r = Number.isFinite(u) ? Math.min(Math.max(u, 0), 1) : 0.5;
  return baseMs * (1 + j * (2 * r - 1));
}

/** The longest wait between two `full` answers before jitter (protocol §3, architecture §6.3). */
export const FULL_RETRY_MAX_MS = 30_000;
/** The first wait after a `full` answer that names none. */
export const FULL_RETRY_DEFAULT_SEC = 5;

/**
 * The wait after `full` answer number `n` (1-based): max(retryAfterSec, 1) s × 1.5^(n−1), capped at
 * 30 s, then +0–20 % jitter (`random` in [0, 1)), so it never undercuts the server's ask. Whole ms.
 */
export function fullRetryDelayMs(
  n: number,
  retryAfterSec: number | undefined,
  random: () => number,
): number {
  const k = Math.max(1, Math.floor(n) || 1);
  const ask =
    retryAfterSec !== undefined && Number.isFinite(retryAfterSec) && retryAfterSec > 0
      ? retryAfterSec
      : FULL_RETRY_DEFAULT_SEC;
  const baseMs = Math.min(FULL_RETRY_MAX_MS, Math.max(1, ask) * 1000 * 1.5 ** (k - 1));
  const u = random();
  const r = Number.isFinite(u) ? Math.min(Math.max(u, 0), 1) : 0.5;
  return Math.round(baseMs * (1 + NET_TIMING.retryJitter * r));
}

// --- Connector -------------------------------------------------------------------------------

export type ConnectTarget =
  | { readonly kind: 'mode'; readonly mode: ModeId }
  | { readonly kind: 'party'; readonly code: string }
  | { readonly kind: 'money'; readonly stakeUsd: number }
  /** The practice arena (money-mode §1.4): /api/practice/find, anonymous like free play. */
  | { readonly kind: 'practice' };

/** Why the connector stopped retrying (the app shows the matching ui-flow §9 dialog). */
export type GiveUpReason =
  | 'cantReach'
  | 'roomFull'
  | 'outdated'
  | 'kicked'
  | 'tooMany'
  | 'partyGone'
  /** Money target: /api/money/find answered `disabled` (money off or the stake not offered). */
  | 'moneyOff'
  /** Money target: closed 4011–4016 after ERROR 11–16 (the app already shows why). */
  | 'moneyRefused'
  /** Money target: closed 4017 (the access token refused) twice in a row. */
  | 'authInvalid'
  /** Practice target: /api/practice/find answered `disabled` (practice off on this server). */
  | 'practiceOff';

export interface ConnectorEvents {
  /** An attempt started (the play card shows "Finding a room…"). */
  connecting: { readonly attempt: number };
  /** WELCOME arrived. `afterRoomFull` = this connection replaced one closed with 4002. */
  connected: {
    readonly welcome: WelcomeMessage;
    readonly target: ConnectTarget;
    readonly afterRoomFull: boolean;
  };
  /** A working connection closed unexpectedly; retries follow automatically. */
  lost: { readonly code: number };
  /** Waiting before the next retry. */
  retrying: { readonly retry: number; readonly delayMs: number };
  /**
   * The server answered `full` (every room taken, or its capacity guard refused): waiting
   * `delayMs` before asking again. `retry` counts the full answers in a row (1-based).
   * `retrying` false (money targets): the connector has stopped instead; the app owns the wait.
   * `retryAfterSec` is the server's hint, when it sent one.
   */
  full: {
    readonly retry: number;
    readonly delayMs: number;
    readonly capacity: boolean;
    readonly retrying: boolean;
    readonly retryAfterSec?: number;
  };
  gaveUp: { readonly reason: GiveUpReason };
}

export interface ConnectorOptions extends HttpOptions {
  /**
   * Money targets: the access token for HELLO (null = connect without one). `fresh` after a
   * 4017: do not reuse a cached token. Absent = never send a token.
   */
  readonly authToken?: (opts: { readonly fresh: boolean }) => Promise<string | null>;
  /** Jitter source. Default Math.random (cosmetic client code). */
  readonly random?: () => number;
  readonly setTimeout?: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout?: (handle: unknown) => void;
}

/** Retries after the first attempt before giving up with 'cantReach' (protocol §3). */
export const MAX_RETRIES = NET_TIMING.retryWaitsSec.length;

/** Whole-attempt timeout: lookup + open + WELCOME (protocol §3). */
export const ATTEMPT_TIMEOUT_MS = NET_TIMING.connectAttemptTimeoutSec * 1000;

export const CONNECTOR_PHASE = {
  IDLE: 'idle',
  /** /api/find or the party lookup is running. */
  FINDING: 'finding',
  /** The socket is opening; waiting for WELCOME. */
  OPENING: 'opening',
  CONNECTED: 'connected',
  /** Waiting before a retry (after a failure, or a `full` answer). */
  WAITING: 'waiting',
  GAVE_UP: 'gaveUp',
  STOPPED: 'stopped',
} as const;
export type ConnectorPhase = (typeof CONNECTOR_PHASE)[keyof typeof CONNECTOR_PHASE];

const GIVE_UP_CODES: Readonly<Record<number, GiveUpReason>> = {
  [CLOSE.VERSION_MISMATCH]: 'outdated',
  [CLOSE.ROOM_NOT_FOUND]: 'partyGone',
  [CLOSE.KICKED]: 'kicked',
  [CLOSE.TOO_MANY_CONNECTIONS]: 'tooMany',
  [CLOSE.TICKET_REQUIRED]: 'moneyRefused',
  [CLOSE.TICKET_INVALID]: 'moneyRefused',
  [CLOSE.TICKET_USED]: 'moneyRefused',
  [CLOSE.MONEY_LIFE_ACTIVE]: 'moneyRefused',
  [CLOSE.STAKE_NOT_OFFERED]: 'moneyRefused',
  [CLOSE.TICKET_NOT_YOURS]: 'moneyRefused',
};

type LookupResult =
  | { readonly status: 'ok'; readonly wsUrl: string }
  | ApiFull
  | { readonly status: 'notFound' }
  | { readonly status: 'disabled' };

export class Connector {
  readonly events = new Emitter<ConnectorEvents>();
  readonly socket: GameSocket;
  /** The room target currently wanted (null before the first connect). */
  target: ConnectTarget | null = null;
  protected readonly opts: ConnectorOptions;

  private phaseValue: ConnectorPhase = CONNECTOR_PHASE.IDLE;
  /** Bumped by every new attempt, connect, retry, stop and give-up: stale callbacks see a mismatch. */
  private gen = 0;
  /** Attempts since the last connect()/retry() (the `connecting` event's number). */
  private attempts = 0;
  /** Consecutive failed retries (protocol §3); reset by WELCOME. */
  private retries = 0;
  /** `full` answers in a row (servers-full backoff); reset by WELCOME and connect/retry. */
  private fullAnswers = 0;
  /** 4002 closes in a row (ui-flow §9). */
  private roomFullStreak = 0;
  /** What ends the 4002 streak: the replacement's WELCOME (join refused) or a SPAWNED (PLAY refused). */
  private roomFullEndsOn: 'welcome' | 'spawned' = 'welcome';
  /** The running attempt replaces a connection closed with 4002. */
  private afterRoomFull = false;
  /** The last close was 4006: waits are at least 2 s. */
  private afterShutdown = false;
  /** A 4017 was answered with one reconnect (a fresh token); the next one gives up. */
  private authRetried = false;
  /** The next money attempt asks for a fresh token (after a 4017). */
  private freshToken = false;
  private timer: unknown = null;
  /** Set while this connector closes the socket itself (those close events are not losses). */
  private closingOwn = false;
  private readonly unsubscribe: (() => void)[];
  private readonly setT: (fn: () => void, ms: number) => unknown;
  private readonly clearT: (handle: unknown) => void;
  private readonly random: () => number;

  constructor(socket: GameSocket, opts: ConnectorOptions = {}) {
    this.socket = socket;
    this.opts = opts;
    this.setT = opts.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
    this.clearT =
      opts.clearTimeout ??
      ((h) => globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>));
    this.random = opts.random ?? (() => Math.random());
    this.unsubscribe = [
      socket.events.on('welcome', (w) => this.onWelcome(w)),
      socket.events.on('close', (c) => this.onClose(c)),
      socket.events.on('spawned', () => {
        if (this.roomFullEndsOn === 'spawned') this.roomFullStreak = 0;
      }),
    ];
  }

  /** Where the connector is in its cycle. */
  get phase(): ConnectorPhase {
    return this.phaseValue;
  }

  /** Consecutive failed retries so far (0 while connected). */
  get retryCount(): number {
    return this.retries;
  }

  /** `full` answers in a row so far (0 once a room was found). */
  get fullCount(): number {
    return this.fullAnswers;
  }

  /** Switch to `target`: close the current socket (1000) and start a first attempt at once. */
  connect(target: ConnectTarget): void {
    this.target = target;
    this.restart();
  }

  /** The dialog's Retry: reset the retry counter and start a new first attempt. */
  retry(): void {
    if (!this.target) return;
    this.restart();
  }

  /** Stop all attempts and timers, close the socket. */
  stop(): void {
    this.gen++;
    this.clearTimer();
    this.phaseValue = CONNECTOR_PHASE.STOPPED;
    this.closeSocket();
  }

  /** stop() and detach from the socket for good. */
  dispose(): void {
    this.stop();
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.events.clear();
  }

  // -------------------------------------------------------------------------------------------

  private restart(): void {
    this.gen++;
    this.clearTimer();
    this.retries = 0;
    this.fullAnswers = 0;
    this.attempts = 0;
    this.roomFullStreak = 0;
    this.afterRoomFull = false;
    this.afterShutdown = false;
    this.authRetried = false;
    this.freshToken = false;
    this.closeSocket();
    this.startAttempt();
  }

  private startAttempt(): void {
    const target = this.target;
    if (!target) return;
    const gen = ++this.gen;
    this.clearTimer();
    this.phaseValue = CONNECTOR_PHASE.FINDING;
    this.attempts++;
    this.events.emit('connecting', { attempt: this.attempts });
    if (gen !== this.gen) return; // a listener switched target or stopped us
    this.timer = this.setT(() => {
      if (gen !== this.gen) return;
      this.timer = null;
      this.attemptFailed();
    }, ATTEMPT_TIMEOUT_MS);
    const lookup: Promise<LookupResult> =
      target.kind === 'mode'
        ? findRoom(target.mode, this.opts)
        : target.kind === 'party'
          ? lookupParty(target.code, this.opts)
          : target.kind === 'practice'
            ? findPracticeRoom(this.opts)
            : findMoneyRoom(target.stakeUsd, this.opts);
    lookup.then(
      (res) => {
        if (gen !== this.gen || this.phaseValue !== CONNECTOR_PHASE.FINDING) return;
        if (res.status === 'ok') {
          const tokenFn = this.opts.authToken;
          if (target.kind !== 'money' || !tokenFn) {
            this.open(res.wsUrl, undefined);
            return;
          }
          // Still FINDING while the token comes (the attempt timeout keeps running).
          const fresh = this.freshToken;
          this.freshToken = false;
          void Promise.resolve()
            .then(() => tokenFn({ fresh }))
            .catch(() => null)
            .then((token) => {
              if (gen !== this.gen || this.phaseValue !== CONNECTOR_PHASE.FINDING) return;
              this.open(res.wsUrl, token ?? undefined);
            });
        } else if (res.status === 'full') {
          if (target.kind === 'party') this.giveUp('roomFull');
          else this.scheduleFullRetry(res, target.kind === 'mode' || target.kind === 'practice');
        } else if (res.status === 'disabled') {
          this.giveUp(target.kind === 'practice' ? 'practiceOff' : 'moneyOff');
        } else {
          this.giveUp('partyGone');
        }
      },
      () => {
        if (gen !== this.gen || this.phaseValue !== CONNECTOR_PHASE.FINDING) return;
        this.attemptFailed();
      },
    );
  }

  private open(wsUrl: string, authToken: string | undefined): void {
    this.phaseValue = CONNECTOR_PHASE.OPENING;
    this.closingOwn = true;
    try {
      this.socket.connect(wsUrl, authToken);
    } finally {
      this.closingOwn = false;
    }
  }

  private onWelcome(w: WelcomeMessage): void {
    if (this.phaseValue !== CONNECTOR_PHASE.OPENING || !this.target) return;
    this.clearTimer();
    this.phaseValue = CONNECTOR_PHASE.CONNECTED;
    this.retries = 0;
    this.fullAnswers = 0;
    this.afterShutdown = false;
    this.authRetried = false;
    if (this.roomFullEndsOn === 'welcome') this.roomFullStreak = 0;
    const afterRoomFull = this.afterRoomFull;
    this.afterRoomFull = false;
    this.events.emit('connected', { welcome: w, target: this.target, afterRoomFull });
  }

  private onClose(c: SocketCloseInfo): void {
    if (this.closingOwn) return;
    const phase = this.phaseValue;
    if (phase !== CONNECTOR_PHASE.OPENING && phase !== CONNECTOR_PHASE.CONNECTED) return;
    const wasConnected = phase === CONNECTOR_PHASE.CONNECTED;
    this.clearTimer();
    const code = c.code;

    if (c.byClient && code === CLOSE.NORMAL) {
      // Someone else closed the socket on purpose (ui-flow §9: 1000 → no reaction).
      this.gen++;
      this.phaseValue = CONNECTOR_PHASE.STOPPED;
      return;
    }
    if (code === CLOSE.ROOM_FULL) {
      if (this.roomFullStreak >= 1) {
        this.giveUp('roomFull');
        return;
      }
      this.roomFullStreak = 1;
      this.roomFullEndsOn = c.welcomed ? 'spawned' : 'welcome';
      this.afterRoomFull = true;
      this.startAttempt();
      return;
    }
    this.roomFullStreak = 0;
    this.afterRoomFull = false;

    if (code === CLOSE.AUTH_INVALID && this.target?.kind === 'money') {
      // The HELLO token was refused: one reconnect with a fresh token, then give up.
      if (this.authRetried) {
        this.giveUp('authInvalid');
        return;
      }
      this.authRetried = true;
      this.freshToken = true;
      this.startAttempt();
      return;
    }

    const giveUp = GIVE_UP_CODES[code];
    if (giveUp) {
      this.giveUp(giveUp);
      return;
    }
    this.afterShutdown = code === CLOSE.SHUTTING_DOWN;
    if (wasConnected) {
      this.events.emit('lost', { code });
      if (this.phaseValue !== CONNECTOR_PHASE.CONNECTED) return; // a listener took over
    }
    this.scheduleRetry();
  }

  private attemptFailed(): void {
    this.clearTimer();
    this.closeSocket();
    this.scheduleRetry();
  }

  private scheduleRetry(): void {
    if (this.retries >= MAX_RETRIES) {
      this.giveUp('cantReach');
      return;
    }
    this.retries++;
    const delayMs = retryDelayMs(this.retries, this.random, this.afterShutdown);
    const gen = ++this.gen;
    this.phaseValue = CONNECTOR_PHASE.WAITING;
    this.events.emit('retrying', { retry: this.retries, delayMs });
    if (gen !== this.gen) return;
    this.timer = this.setT(() => {
      if (gen !== this.gen) return;
      this.timer = null;
      this.startAttempt();
    }, delayMs);
  }

  /**
   * The server answered `full`: wait (growing) and ask again; never gives up by itself. The
   * answer proves the server reachable, so the failed-retry count starts over. With `retry`
   * false (money targets) it stops instead and leaves the wait to the app.
   */
  private scheduleFullRetry(res: ApiFull, retry: boolean): void {
    this.clearTimer();
    this.retries = 0;
    this.afterShutdown = false;
    this.fullAnswers++;
    const delayMs = fullRetryDelayMs(this.fullAnswers, res.retryAfterSec, this.random);
    const gen = ++this.gen;
    this.phaseValue = retry ? CONNECTOR_PHASE.WAITING : CONNECTOR_PHASE.STOPPED;
    if (!retry) {
      this.roomFullStreak = 0;
      this.afterRoomFull = false;
      this.closeSocket();
    }
    this.events.emit('full', {
      retry: this.fullAnswers,
      delayMs,
      capacity: res.reason === 'capacity',
      retrying: retry,
      ...(res.retryAfterSec !== undefined ? { retryAfterSec: res.retryAfterSec } : {}),
    });
    if (!retry || gen !== this.gen) return;
    this.timer = this.setT(() => {
      if (gen !== this.gen) return;
      this.timer = null;
      this.startAttempt();
    }, delayMs);
  }

  private giveUp(reason: GiveUpReason): void {
    this.gen++;
    this.clearTimer();
    this.phaseValue = CONNECTOR_PHASE.GAVE_UP;
    this.roomFullStreak = 0;
    this.afterRoomFull = false;
    this.closeSocket();
    this.events.emit('gaveUp', { reason });
  }

  private closeSocket(): void {
    this.closingOwn = true;
    try {
      this.socket.close(CLOSE.NORMAL);
    } finally {
      this.closingOwn = false;
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      this.clearT(this.timer);
      this.timer = null;
    }
  }
}
